import { useEffect, useMemo, useState, Fragment, type ReactNode } from 'react';
// Fully Tailwind now — toolbar controls (density / column-picker) + pagination use the ui/ kit
// (no Ant Design). Design tokens from styles/theme.css → theme-aware + dark-mode correct.
import { Button, Dropdown, Checkbox, IconButton, Pagination } from '../ui';
import { useDragScroll } from '../ui/useDragScroll';
import { ExpandedRow } from '../ui/ExpandedRow';
import { Settings, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import type { DataTableColumn } from './DataTable';

export type { DataTableColumn } from './DataTable';

// ── Tailwind pill (Tag replacement) — exported so converted pages can use it in cell renders ──────
const PILL: Record<string, string> = {
  default: 'bg-surface-3 text-muted border-line',
  green: 'bg-brand-500/12 text-brand-700 dark:text-brand-300 border-brand-500/25',
  blue: 'bg-sky-500/12 text-sky-600 dark:text-sky-400 border-sky-500/25',
  gold: 'bg-harvest-400/18 text-harvest-700 dark:text-harvest-300 border-harvest-400/35',
  orange: 'bg-warning-500/15 text-warning-600 dark:text-harvest-300 border-warning-500/30',
  red: 'bg-danger-500/12 text-danger-600 dark:text-danger-500 border-danger-500/30',
  purple: 'bg-violet-500/12 text-violet-600 dark:text-violet-400 border-violet-500/25',
  lime: 'bg-lime-500/15 text-lime-700 dark:text-lime-400 border-lime-500/30',
};
export function Pill({ color = 'default', className = '', children }: { color?: string; className?: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-pill border px-2 py-px type-label leading-5 ${PILL[color] ?? PILL.default} ${className}`}>
      {children}
    </span>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"
      className={`h-3.5 w-3.5 shrink-0 text-subtle transition-transform duration-150 ${open ? 'rotate-90' : ''}`}>
      <path d="M6 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SortArrows({ dir }: { dir: 'asc' | 'desc' | null }) {
  // Faint up/down arrows when the column isn't the active sort; a single green ↑/↓ when it is.
  // (Spacing comes from the header's `gap-1` so it stays correct even when the header is reversed
  // for right-aligned columns — a margin here would land on the wrong side and touch the label.)
  const cls = 'size-3.5 shrink-0';
  if (dir === 'asc') return <ArrowUp className={`${cls} text-brand-600 dark:text-brand-400`} strokeWidth={2.5} />;
  if (dir === 'desc') return <ArrowDown className={`${cls} text-brand-600 dark:text-brand-400`} strokeWidth={2.5} />;
  return <ArrowUpDown className={`${cls} text-line-strong`} strokeWidth={2} />;
}

type Density = 'small' | 'middle' | 'large';
const PAD: Record<Density, string> = { small: 'px-3 py-1.5', middle: 'px-3 py-2.5', large: 'px-4 py-3.5' };
// An `autoFit` table sizes its columns from their own content, so the cell padding IS the gap
// between two columns. At the default px-3 that leaves only 24px wherever a right-aligned figure
// meets a left-aligned one — the two values read as touching. Widen the gutter so no pair can
// collide, and so the leftover width lands as breathing room between columns rather than as one
// dead strip at the end of the row.
const autoFitPad = (p: string) => p.replace(/px-\d+(\.\d+)?/, 'px-6');

interface TwExpandable<T> {
  rowExpandable?: (r: T) => boolean;
  expandRowByClick?: boolean;
  childrenColumnName?: string;
  /**
   * Set false when expansion is CONTROLLED by the caller and there is nothing to toggle.
   *
   * This used to be accepted and ignored, which left a chevron that did nothing: the Pick & pack
   * allocator opens a line automatically once it has a quantity, so its toggle updated internal state
   * the controlled `expandedRowKeys` then overrode. A control that cannot do anything must not be drawn.
   */
  showExpandColumn?: boolean;
  defaultExpandAllRows?: boolean;
  /** Custom content rendered in a full-width row below the parent when expanded. */
  expandedRowRender?: (r: T) => ReactNode;
  /** Controlled expansion — when provided, the table reflects these keys + reports changes. */
  expandedRowKeys?: (string | number)[];
  onExpandedRowsChange?: (keys: (string | number)[]) => void;
}

interface TwDataTableProps<T> {
  storageKey: string;
  columns: DataTableColumn<T>[];
  dataSource: T[];
  rowKey: string | ((r: T, index?: number) => string);
  loading?: boolean;
  pagination?: false | { pageSize?: number; showSizeChanger?: boolean; pageSizeOptions?: number[]; hideOnSinglePage?: boolean; showTotal?: (total: number, range: [number, number]) => ReactNode; size?: 'small' | 'default'; current?: number; total?: number; onChange?: (page: number, pageSize: number) => void };
  expandable?: TwExpandable<T>;
  /** antd-style row selection (leading checkbox/radio column). Controlled via selectedRowKeys. */
  rowSelection?: {
    selectedRowKeys?: (string | number)[];
    onChange?: (selectedKeys: (string | number)[], selectedRows: T[]) => void;
    type?: 'checkbox' | 'radio';
    getCheckboxProps?: (r: T) => { disabled?: boolean };
  };
  onRow?: (r: T) => { onClick?: (e?: any) => void; style?: any; className?: string } | undefined;
  rowClassName?: (r: T) => string;
  /** Controlled SERVER-side sort. When `onSortChange` is provided the table STOPS sorting locally
   *  (the server returns already-ordered rows), reflects `sortState` in the header arrows, and
   *  reports header clicks up so the page can push a `sort` query param and refetch. Omit both to
   *  keep the default client-side sort (correct for tables that load their full dataset). */
  sortState?: { key: string; dir: 'asc' | 'desc' } | null;
  onSortChange?: (next: { key: string; dir: 'asc' | 'desc' } | null) => void;
  /** antd-style footer summary — receives the page rows, returns <tr>…</tr> content. */
  summary?: (data: T[]) => ReactNode;
  toolbarLeft?: ReactNode;
  toolbarRight?: ReactNode;
  /** Rendered on its own row directly under the toolbar, still inside the table's card — for an
   *  expanding filter panel, which belongs with the toolbar that opens it rather than floating
   *  over the rows it is filtering. */
  toolbarBelow?: ReactNode;
  emptyText?: ReactNode;
  /** Hide the density + column-picker toolbar (for small / inline / modal tables). Default: shown. */
  showToolbar?: boolean;
  /** Drop the outer card border/shadow (for tables embedded in an already-bordered panel). */
  bordered?: boolean;
  /** Initial row density when the user hasn't chosen one (no persisted value). Lets the adapters map
   *  their antd `size` (small/middle/large) onto the density so `size="small"` actually renders dense. */
  initialDensity?: Density;
  /** `table-layout: fixed` — distributes leftover width EVENLY across columns (per their `width`s)
   *  instead of letting one width-less column soak up all the slack (the "airy gap" look). Give every
   *  column a `width` when you use this. */
  layoutFixed?: boolean;
  /** AUTO column widths with a WIDE gutter. Every column is sized from its own content (heading
   *  included, so headings sit on one line) and the leftover width is shared out across the columns
   *  in proportion to that content — the row fills the table and the slack becomes space BETWEEN the
   *  columns. Cells also get a wider gutter (see `autoFitPad`), because with content-sized columns
   *  that padding is what separates one value from the next.
   *
   *  Use it instead of `layoutFixed` when the columns hold SHORT values under long headings and you
   *  don't want to hand-tune a width for each one. DO set `align: 'center'` on short numeric columns
   *  so the value sits under its heading rather than against one edge, and don't set `width` on the
   *  columns (that would opt them out of the sharing). Mutually exclusive with `layoutFixed`. */
  autoFit?: boolean;
}

export function TwDataTable<T extends Record<string, any>>({
  storageKey, columns, dataSource, rowKey, loading, pagination, expandable, rowSelection, onRow, rowClassName, summary, toolbarLeft, toolbarRight, toolbarBelow, emptyText, showToolbar = true, bordered = true, initialDensity, sortState, onSortChange, layoutFixed, autoFit,
}: TwDataTableProps<T>) {
  const keyFor = (c: DataTableColumn<T>) =>
    c.key ?? (Array.isArray(c.dataIndex) ? c.dataIndex.join('.') : String(c.dataIndex ?? c.title));
  const allKeys = useMemo(() => columns.map(keyFor), [columns]);
  const defaultVisible = useMemo(() => columns.filter((c) => c.defaultVisible !== false).map(keyFor), [columns]);

  // ── Column visibility + density, persisted (same scheme as the Antd DataTable) ──
  const visKey = `gb:tbl:${storageKey}:visible`;
  const densKey = `gb:tbl:${storageKey}:density`;
  const knownKey = `gb:tbl:${storageKey}:known`;
  const alwaysKeys = useMemo(() => columns.filter((c) => c.alwaysVisible).map(keyFor), [columns]);

  /**
   * The saved layout for THIS table, repaired if it was written by a different one.
   *
   * TWO shapes are rejected, both meaning "no reader chose this":
   *
   * 1. It carries a key this table does not have — it came from another column set (see the re-seed
   *    below), and filtering it against these columns leaves whatever the two sets happen to share.
   *    The honest false positive is a column REMOVED in a later release: that reader's layout resets
   *    once, which is the right answer anyway.
   * 2. It leaves the table with NOTHING but its mandatory columns. This is the wreckage of (1)
   *    rather than a fresh instance of it: the old code persisted the FILTERED list, so by the time
   *    a reader saw the damage the foreign keys that would prove it had already been written away —
   *    a register stuck on one column with an empty array behind it and no evidence left. A reader
   *    CAN untick every optional column by hand and would land here too; they get the defaults back
   *    and can untick again, which is a far better failure than a register that reads as blank on
   *    every visit for good.
   */
  const seedVisible = () => {
    try {
      const raw = localStorage.getItem(visKey);
      if (!raw) return defaultVisible;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return defaultVisible;
      if (parsed.some((k: string) => !allKeys.includes(k))) return defaultVisible;
      const optional = allKeys.filter((k) => !alwaysKeys.includes(k));
      if (optional.length && !parsed.some((k: string) => optional.includes(k))) return defaultVisible;
      const vis = parsed.filter((k: string) => allKeys.includes(k));
      let known: string[];
      try { const k = localStorage.getItem(knownKey); known = k ? (JSON.parse(k) || []) : vis; } catch { known = vis; }
      const fresh = defaultVisible.filter((k) => !known.includes(k) && !vis.includes(k));
      return fresh.length ? [...vis, ...fresh] : vis;
    } catch { return defaultVisible; }
  };
  const [visible, setVisible] = useState<string[]>(seedVisible);

  /*
   * RE-SEED WHEN THE TABLE CHANGES IDENTITY.
   *
   * One mounted table can serve several column sets — Coverage renders the same `TwDataTable` for
   * its customer / territory / staff grains, handing it a different `storageKey` and a different
   * `columns` each time. Visibility was seeded once at mount, so switching grain kept the PREVIOUS
   * grain's key list: filtered against the new columns it left only the keys the two sets happen to
   * share (`repName`, `territoryName`), and the persistence effect below then wrote that foreign
   * list into the new grain's storage — so the collapse survived a reload and every later visit. A
   * reader who opened By staff after By customer got a register of one column and no idea why.
   *
   * This is the documented "adjust state when a prop changes" pattern: the render that notices is
   * thrown away and re-run with the right value, so the wrong list is never shown and never saved.
   */
  const [seededFor, setSeededFor] = useState(visKey);
  if (seededFor !== visKey) {
    setSeededFor(visKey);
    setVisible(seedVisible());
  }
  // Row density is no longer user-switchable (the Compact/Default/Spacious toggle was removed from
  // every table): a caller can still ask for a denser table via `initialDensity` — used by the small
  // inline/modal tables — but the page decides, not a control in the toolbar. Any density a user had
  // previously persisted is deliberately ignored, so nobody is left stuck on a setting they can no
  // longer reach; the stale key is cleared on sight.
  const density: Density = initialDensity ?? 'middle';
  // Column-visibility state is only meaningful when there's a TOOLBAR (the column picker the user drives).
  // Without one (modal/inline tables like the approve dialog) we neither persist nor filter by it — see `cols`.
  useEffect(() => { if (showToolbar) localStorage.setItem(visKey, JSON.stringify(visible)); }, [visKey, visible, showToolbar]);
  useEffect(() => { localStorage.removeItem(densKey); }, [densKey]);
  // Reconcile visibility when the column set changes AT RUNTIME (dynamic/conditional columns — e.g. an
  // approve modal that adds "Committed"/"Free here" once its async stock-plan resolves). The `visible`
  // state is seeded once at mount, so a column that appears later would otherwise be filtered out. Here
  // we auto-show any BRAND-NEW default-visible column (one never recorded in `known`), then record every
  // current column as known. Columns the user explicitly hid are already in `known`, so they stay hidden.
  useEffect(() => {
    if (!showToolbar) return; // no picker → visibility isn't user-managed; nothing to reconcile/persist
    let known: string[] = [];
    try { const k = localStorage.getItem(knownKey); known = k ? (JSON.parse(k) || []) : []; } catch { known = []; }
    const brandNew = defaultVisible.filter((key) => !known.includes(key));
    if (brandNew.length) setVisible((cur) => { const add = brandNew.filter((key) => !cur.includes(key)); return add.length ? [...cur, ...add] : cur; });
    const merged = Array.from(new Set([...known, ...allKeys]));
    if (merged.length !== known.length) { try { localStorage.setItem(knownKey, JSON.stringify(merged)); } catch { /* ignore */ } }
  }, [knownKey, allKeys, defaultVisible, showToolbar]);
  const visibleSet = useMemo(() => new Set(visible), [visible]);
  /*
   * Columns the reader has switched OFF — measured against the table's default layout, not against
   * every column that exists.
   *
   * A column marked `defaultVisible: false` is off because the table meant it to be off: an opt-in
   * extra for the one reader who wants it. Counting those would put a permanent "Show 2 hidden
   * columns" on tables nobody has touched, which is furniture — the mark has to VARY to mean
   * anything. So only a column that normally shows and is currently hidden counts, and revealing
   * them restores exactly that default set rather than also dragging in the opt-ins.
   */
  const hiddenDefaults = useMemo(
    () => defaultVisible.filter((k) => !visibleSet.has(k) && !columns.some((c) => keyFor(c) === k && c.alwaysVisible)),
    [defaultVisible, visibleSet, columns],
  );
  // WITH a toolbar: honour the user's persisted show/hide choices. WITHOUT one: always render the current
  // default-visible columns (the persisted set can only be stale/programmatic there, never a user choice),
  // so a dynamically-added column can never be hidden by leftover localStorage.
  const cols = columns.filter((c) => c.alwaysVisible || (showToolbar ? visibleSet.has(keyFor(c)) : c.defaultVisible !== false));

  /**
   * How wide the table WANTS to be — the sum of what its visible columns asked for.
   *
   * `w-full` with the browser's default auto layout treats a column's `width` as a hint, then
   * squeezes every column to fit the container. Switch on a dozen optional columns and you get a
   * cram of 40px cells with truncated headers ("2…" where a GST number should be) instead of a
   * table you scroll sideways. The container has always been `overflow-x-auto`; it just never had
   * anything to scroll, because the table kept shrinking itself to fit.
   *
   * Deriving the minimum from the VISIBLE set means the table grows as columns are revealed and
   * shrinks back as they are hidden — no page has to guess a fixed `scroll={{x}}` number that is
   * wrong for every combination but one.
   */
  const naturalWidth = useMemo(() => {
    const DEFAULT_COL = 150; // a column that declares no width still needs room to stay readable
    return cols.reduce((sum, c) => sum + (typeof c.width === 'number' ? c.width : DEFAULT_COL), 0)
      + (rowSelection ? 40 : 0) + (expandable && expandable.showExpandColumn !== false ? 40 : 0);
  }, [cols, rowSelection, expandable]);
  /**
   * Honour the declared widths instead of letting content redistribute them — but never override an
   * `autoFit` table, whose whole point is content-sized columns, and only when every visible column
   * actually declares a width (fixed layout would otherwise hand the leftovers out arbitrarily).
   */
  const useFixed = layoutFixed || (!autoFit && cols.length > 0 && cols.every((c) => c.width != null));
  const { ref: scrollRef, pannable } = useDragScroll<HTMLDivElement>();

  // ── Sorting — cycles asc → desc → off. Client-side by default; SERVER-side (controlled) when the
  // caller passes `onSortChange`: the table then reflects `sortState`, reports clicks up, and does
  // NOT re-order locally (the server already returned the whole dataset in order). ──
  const controlledSort = !!onSortChange;
  const [sortInternal, setSortInternal] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const sort = controlledSort ? (sortState ?? null) : sortInternal;
  const defaultSorter = (c: DataTableColumn<T>) => (a: T, b: T) => {
    const pick = (o: any) => (Array.isArray(c.dataIndex) ? c.dataIndex.reduce((x: any, k) => x?.[k], o) : o[c.dataIndex as string]);
    const ka = pick(a); const kb = pick(b);
    if (ka == null && kb == null) return 0;
    if (ka == null) return -1;
    if (kb == null) return 1;
    if (typeof ka === 'number' && typeof kb === 'number') return ka - kb;
    return String(ka).localeCompare(String(kb), undefined, { numeric: true });
  };
  const onSort = (c: DataTableColumn<T>) => {
    const k = keyFor(c);
    const next: { key: string; dir: 'asc' | 'desc' } | null =
      sort?.key !== k ? { key: k, dir: 'asc' } : sort.dir === 'asc' ? { key: k, dir: 'desc' } : null;
    if (controlledSort) onSortChange!(next); else setSortInternal(next);
  };
  const sorted = useMemo(() => {
    if (controlledSort || !sort) return dataSource;   // server already ordered the full set (or no sort)
    const col = columns.find((c) => keyFor(c) === sort.key);
    if (!col) return dataSource;
    const sorter = col.sorter ?? defaultSorter(col);
    const arr = [...dataSource].sort(sorter);
    return sort.dir === 'desc' ? arr.reverse() : arr;
  }, [dataSource, sort, columns, controlledSort]);

  // ── Pagination ──
  // Two modes: CLIENT (default) — we slice `sorted` locally and own page/pageSize;
  // SERVER/controlled — the caller passes `current`/`total` (+ onChange) and hands us
  // only the current page's rows, so we render them verbatim and report page changes up.
  const pageObj = pagination === false ? undefined : pagination;
  const serverMode = !!pageObj && (pageObj.current != null || pageObj.total != null);
  const [page, setPage] = useState(1);
  const [pageSizeInternal, setPageSize] = useState<number>(pageObj?.pageSize ?? 10);
  const pageSize = serverMode ? (pageObj?.pageSize ?? pageSizeInternal) : pageSizeInternal;
  const total = serverMode ? (pageObj?.total ?? sorted.length) : sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = serverMode ? (pageObj?.current ?? 1) : Math.min(page, pageCount);
  const paged = pagination === false || serverMode ? sorted : sorted.slice((current - 1) * pageSize, current * pageSize);

  // ── Expansion (tree-data children) ──
  const childrenKey = expandable?.childrenColumnName ?? 'children';
  // The toggle column exists unless the caller explicitly says it should not.
  const expandCol = !!expandable && expandable.showExpandColumn !== false;
  const controlled = expandable?.expandedRowKeys != null;
  const [openInternal, setOpenInternal] = useState<Set<string>>(new Set());
  const openSet = controlled ? new Set((expandable!.expandedRowKeys ?? []).map(String)) : openInternal;
  const getKey = (r: T, i?: number) => (typeof rowKey === 'function' ? rowKey(r, i) : String(r[rowKey]));
  const toggle = (k: string) => {
    if (controlled) {
      const cur = (expandable!.expandedRowKeys ?? []).map(String);
      const next = cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k];
      expandable!.onExpandedRowsChange?.(next);
    } else {
      setOpenInternal((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
    }
  };
  const expandable_ = (r: T) => !!expandable && (expandable.rowExpandable ? expandable.rowExpandable(r) : (expandable.expandedRowRender ? true : Array.isArray((r as any)[childrenKey])));

  // ── Row selection (leading checkbox/radio column; controlled via selectedRowKeys) ──
  const selKeys = new Set((rowSelection?.selectedRowKeys ?? []).map(String));
  const selectableRows = rowSelection ? sorted.filter((r) => !rowSelection.getCheckboxProps?.(r)?.disabled) : [];
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selKeys.has(getKey(r)));
  const someSelected = !allSelected && selectableRows.some((r) => selKeys.has(getKey(r)));
  const emitSel = (keys: Set<string>) => rowSelection?.onChange?.(Array.from(keys), sorted.filter((r) => keys.has(getKey(r))));
  const toggleRow = (r: T) => {
    const k = getKey(r);
    const next = new Set(selKeys);
    if (rowSelection?.type === 'radio') { next.clear(); next.add(k); }
    else if (next.has(k)) next.delete(k); else next.add(k);
    emitSel(next);
  };
  const toggleAll = () => emitSel(allSelected ? new Set() : new Set(selectableRows.map((r) => getKey(r))));

  const pad = autoFit ? autoFitPad(PAD[density]) : PAD[density];
  const cellAlign = (c: DataTableColumn<T>) => (c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left');
  const cellValue = (c: DataTableColumn<T>, r: T, i: number) => {
    const raw = c.dataIndex ? (Array.isArray(c.dataIndex) ? c.dataIndex.reduce((o: any, k) => o?.[k], r) : (r as any)[c.dataIndex]) : undefined;
    return c.render ? c.render(raw, r, i) : raw;
  };

  const renderRow = (r: T, i: number, child = false) => {
    const k = getKey(r, i);
    const canExpand = expandable_(r);
    const isOpen = openSet.has(k);
    const ro = onRow?.(r);
    const rowClick = () => {
      if (canExpand && expandable?.expandRowByClick) toggle(k);
      else ro?.onClick?.();
    };
    const clickable = (canExpand && expandable?.expandRowByClick) || !!ro?.onClick;
    return (
      <Fragment key={k}>
        <tr
          // Addressable from outside the table: a deep-link (hooks/useFocusRow) scrolls the row it
          // was sent to into view, which needs a way to find it in the DOM.
          data-row-key={k}
          // Hover is a BRAND tint, not a grey one: grey-on-grey reads as "this row is disabled",
          // where a green wash reads as "this row is live under your pointer" — and it matches the
          // header band above it, so the whole table belongs to one colour family.
          className={`border-t border-line/70 transition-colors ${child ? 'bg-surface-2/60' : 'hover:bg-brand-500/[0.055] dark:hover:bg-brand-400/[0.08]'} ${clickable ? 'cursor-pointer' : ''} ${rowClassName?.(r) ?? ''} ${ro?.className ?? ''}`}
          style={ro?.style}
          onClick={clickable ? rowClick : undefined}
        >
          {rowSelection && (
            <td className={`${pad} w-10 align-middle`} onClick={(e) => e.stopPropagation()}>
              {!child && (
                <Checkbox
                  checked={selKeys.has(k)}
                  disabled={rowSelection.getCheckboxProps?.(r)?.disabled}
                  onCheckedChange={() => toggleRow(r)}
                  aria-label="Select row"
                />
              )}
            </td>
          )}
          {expandCol && (
            <td className={`${pad} w-10 align-middle ${child ? 'pl-7' : ''}`} onClick={canExpand ? (e) => { e.stopPropagation(); toggle(k); } : undefined}>
              {canExpand && <Chevron open={isOpen} />}
            </td>
          )}
          {cols.map((c) => {
            const val = cellValue(c, r, i);
            if (c.ellipsis) {
              const rawTitle = typeof val === 'string' || typeof val === 'number' ? String(val) : undefined;
              return (
                <td key={keyFor(c)} className={`${pad} max-w-0 align-middle ${cellAlign(c)}`} style={c.width ? { width: c.width } : undefined}>
                  <div className="truncate" title={rawTitle}>{val}</div>
                </td>
              );
            }
            return (
              <td key={keyFor(c)} className={`${pad} align-middle ${cellAlign(c)}`} style={c.width ? { width: c.width } : undefined}>
                {val}
              </td>
            );
          })}
        </tr>
        {canExpand && isOpen && expandable?.expandedRowRender && (
          // Canonical expanded-row chrome (shared with the ui DataTable + StockPage): a distinct
          // tinted strip, inset + indented under the row body, marked by a left accent rail.
          <ExpandedRow colSpan={colSpanAll}>{expandable.expandedRowRender(r)}</ExpandedRow>
        )}
        {canExpand && isOpen && !expandable?.expandedRowRender && Array.isArray((r as any)[childrenKey]) &&
          ((r as any)[childrenKey] as T[]).map((ch, ci) => renderRow(ch, ci, true))}
      </Fragment>
    );
  };

  const colSpanAll = cols.length + (expandCol ? 1 : 0) + (rowSelection ? 1 : 0);

  return (
    <div>
      {/* Toolbar */}
      {(showToolbar || toolbarLeft || toolbarRight) && (
      // The filter row is a SURFACE, not loose controls on the canvas. Left bare, a date range, a
      // status select and a checkbox read as three unrelated widgets floating above the table; on
      // its own tinted, rounded strip they read as one control panel that belongs to the register
      // underneath. Two cases deliberately keep it bare: `bordered === false` (the caller has
      // already put the table inside a panel of its own, so this would be a box inside a box), and
      // a toolbar with NO caller content — pages that render their own filter panel leave only the
      // column-settings gear here, and giving that a surface draws an empty strip across the page.
      //
      // It is a RECESSED shelf (a shade of the canvas, no shadow), not a raised card. Raised, it
      // competed with the table below it and — on a page whose toolbar holds only a row count —
      // read as an empty white box. Recessed, the white inset fields sitting in it pop, and a
      // sparsely filled shelf still reads as a shelf.
      <div className={`mb-3 flex flex-wrap items-center gap-3 ${bordered && (toolbarLeft || toolbarRight || toolbarBelow) ? 'rounded-card border border-line bg-surface-2/60 px-3 py-2' : ''}`}>
        {/* `min-w-0`: a filter group is sized by its widest control, and a control given an explicit
            width (the visits page asks for a 360px staff picker so long names stay readable) made
            this wrapper max-content wide — which in turn is what a child's `max-w-full` resolves
            against, so the cap could never bite and the toolbar pushed the page sideways on a phone.
            Allowing the wrapper to shrink lets those caps do their job. */}
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{toolbarLeft}</div>
        {/* `shrink-0` beside a `flex-1` left group, rather than a spacer between two auto-width
            groups. With auto widths a filter set wide enough to wrap filled the row on its own, so
            the gear and any toolbar buttons were pushed onto a line of their own — a nearly empty
            strip under the filters, with one icon parked at its right end. Pinned, they stay on the
            first line and the filters wrap inside whatever width is left. */}
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {toolbarRight}
          {/*
            WHAT THIS REGISTER IS NOT SHOWING YOU, and the way to get it back.
            
            Hidden columns are silent by design — the gear remembers a layout per table, and a reader
            who switched three columns off does not need reminding. But the setting outlives the
            afternoon it was made in: come back a month later and a register down to its two
            mandatory columns looks like a page with nothing on it and acres of white space, with no
            hint that ten columns of exactly the data you came for are one click away.
            
            So the table states the fact, quietly, only when there IS something hidden — and the
            statement is itself the control that reveals them.
          */}
          {hiddenDefaults.length > 0 && (
            <Button
              size="sm" variant="text"
              title="Put this register's usual columns back"
              className="px-1! text-brand-700! hover:bg-transparent! dark:text-brand-400!"
              onClick={() => setVisible((v) => Array.from(new Set([...v, ...hiddenDefaults])))}
            >
              Show {hiddenDefaults.length} hidden column{hiddenDefaults.length === 1 ? '' : 's'}
            </Button>
          )}
          <Dropdown
            trigger="click"
            align="end"
            panelClassName="p-0"
            panel={
              <div className="min-w-[210px] p-2.5">
                <div className="mb-2 px-1 type-body-strong text-content">Show columns</div>
                <div className="flex flex-col gap-2 px-1">
                  {columns.map((c) => {
                    const k = keyFor(c);
                    return c.alwaysVisible ? null : (
                      <Checkbox
                        key={k}
                        checked={visible.includes(k)}
                        onCheckedChange={(chk) => setVisible((v) => (chk ? Array.from(new Set([...v, k])) : v.filter((x) => x !== k)))}
                        label={typeof c.title === 'string' && c.title ? c.title : k}
                      />
                    );
                  })}
                </div>
                <div className="mt-2.5 flex gap-3 border-t border-line px-1 pt-2.5">
                  <Button size="sm" variant="text" className="px-0! text-brand-700! hover:bg-transparent! dark:text-brand-400!" onClick={() => setVisible(allKeys)}>Show all</Button>
                  <Button size="sm" variant="text" className="px-0! text-brand-700! hover:bg-transparent! dark:text-brand-400!" onClick={() => setVisible(defaultVisible)}>Reset to default</Button>
                </div>
              </div>
            }
          >
            <IconButton diameter={32} aria-label="Column settings" className="border border-line text-muted hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-900/20">
              <Settings size={16} />
            </IconButton>
          </Dropdown>
        </div>
      </div>
      )}

      {toolbarBelow}

      {/* Table */}
      {/* Grab-and-drag panning. A wide register is otherwise only reachable by trackpad or by hunting
          for a scrollbar that can be a hundred rows below the columns you are trying to read. The
          grab cursor appears only while the content actually overflows. */}
      <div
        ref={scrollRef}
        className={`overflow-x-auto rounded-card ${bordered ? 'border border-line bg-surface shadow-e1' : ''} ${
          pannable ? 'cursor-grab data-[panning=true]:cursor-grabbing data-[panning=true]:select-none' : ''
        }`}
      >
        <table
          className={`w-full border-collapse text-sm ${useFixed ? 'table-fixed' : ''}`}
          style={{ minWidth: naturalWidth }}
        >
          {/* A TINTED band, not a grey one. The original objection to a header fill still stands —
              a grey band made the labels the heaviest thing on a data page, louder than the rows
              they describe — but that is a problem of VALUE, not of colour. A 7% brand wash fading
              into the first row adds no darkness at all (the labels stay muted micro-type over it),
              yet it gives the table a top edge and ties the register to the brand instead of
              leaving a white sheet with a line across it. */}
          <thead className="border-b border-line-strong bg-linear-to-b from-brand-500/[0.14] to-brand-500/[0.04] dark:from-brand-400/[0.13] dark:to-brand-400/[0.03] [&_th]:text-[11px] [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-[0.06em] [&_th]:text-muted">
            <tr>
              {rowSelection && (
                <th className={`${pad} w-10`}>
                  {rowSelection.type !== 'radio' && (
                    <Checkbox
                      checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                      onCheckedChange={toggleAll}
                      aria-label="Select all rows"
                    />
                  )}
                </th>
              )}
              {expandCol && <th className="w-10" />}
              {cols.map((c) => {
                const dir = sort?.key === keyFor(c) ? sort.dir : null;
                return (
                  <th
                    key={keyFor(c)}
                    className={`${pad} type-overline text-muted ${cellAlign(c)} ${c.sortable || c.sorter ? 'cursor-pointer select-none hover:text-content' : ''}`}
                    style={c.width ? { width: c.width } : undefined}
                    onClick={c.sortable || c.sorter ? () => onSort(c) : undefined}
                  >
                    {/* Sort icon ALWAYS trails the label (never flips to the left on right-aligned
                        columns) so the indicator sits on the same side in every column/table. */}
                    <span className="inline-flex items-center gap-1">
                      {c.title}
                      {(c.sortable || c.sorter) && <SortArrows dir={dir} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && !dataSource.length ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-t border-line"><td colSpan={colSpanAll} className="px-3 py-3"><div className="h-6 animate-pulse rounded bg-surface-3" /></td></tr>
              ))
            ) : paged.length ? (
              paged.map((r, i) => renderRow(r, i))
            ) : (
              <tr><td colSpan={colSpanAll} className="px-4 py-12 text-center text-sm text-subtle">{emptyText ?? 'No data'}</td></tr>
            )}
          </tbody>
          {/* The totals row closes the table the way the header opens it — same hue, slightly
              stronger, so "this is the sum of everything above" is visible without reading it. */}
          {summary && paged.length > 0 && <tfoot className="bg-brand-500/[0.08] font-semibold text-content dark:bg-brand-400/[0.10]">{summary(paged)}</tfoot>}
        </table>
      </div>

      {/* Pagination */}
      {pagination !== false && total > 0 && !(pageObj?.hideOnSinglePage && pageCount <= 1) && (
        <div className="mt-3 flex items-center justify-end">
          <Pagination
            size="small"
            current={current}
            pageSize={pageSize}
            total={total}
            showSizeChanger={pageObj?.showSizeChanger ?? true}
            pageSizeOptions={pageObj?.pageSizeOptions}
            onChange={(p, ps) => { if (serverMode) { pageObj?.onChange?.(p, ps); } else { setPage(p); setPageSize(ps); } }}
            showTotal={pageObj?.showTotal ?? ((t) => `${t} rows`)}
          />
        </div>
      )}
    </div>
  );
}
